import { Fragment } from "react";
import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { TrendingUp } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

interface VelocityItem {
  year: number;
  value: number;
}

function buildGrowthLabel(items: VelocityItem[]): string | undefined {
  if (items.length < 2) return undefined;
  const first = items[0].value;
  const last = items[items.length - 1].value;
  if (first <= 0) return undefined;
  const growth = Math.round(((last - first) / first) * 100);
  return `+${growth}% growth since ${items[0].year}`;
}

function VelocityBars({ items, max }: { items: VelocityItem[]; max: number }) {
  if (!items.length) return <LockedContent message={message} />;
  const growthPercentage= buildGrowthLabel(items);

  return (
    <Fragment>
      <div className="space-y-6">
        {items.map((item) => (
          <div key={item.year} className="space-y-2">
            <div className="flex items-baseline justify-between">
              <p className="text-sm text-muted-foreground">{item.year}</p>
              <div className="flex items-baseline gap-1">
                <p className="text-base font-bold text-foreground">{Math.round(item.value)}</p>
                <p className="text-xs text-muted-foreground">assets/mo</p>
              </div>
            </div>
            <Progress
              value={Math.min(100, (item.value / max) * 100)}
              className="h-2 bg-secondary"
              indicatorClassName="bg-gradient-to-r from-blue-400 to-blue-600"
            />
          </div>
        ))}
      </div>
      {growthPercentage && (
        <div className="mt-6 flex items-center gap-2 border-t border-border pt-5 text-sm text-blue-600">
          <TrendingUp className="size-4" strokeWidth={2.5} />
          <p className="font-medium">{growthPercentage}</p>
        </div>
      )}
    </Fragment>
  );
}

function VelocityContent({ status, items, max }: { status: QueryStatus; items: VelocityItem[]; max: number }) {
  switch (status) {
    case "pending":
      return <VelocitySkeleton />;
    case "error":
      return <LockedContent message={message} />;
    case "success":
      return <VelocityBars items={items} max={max} />;
  }
}

export function ContentVelocityTrend() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchIndustryOverview());

  const items = (data?.content_velocity_trend ?? [])
    .filter((i) => i.assets_per_month > 0)
    .map((i) => ({ year: Number(i.year), value: i.assets_per_month }));

  const max = Math.max(...items.map((i) => i.value), 0);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-md bg-blue-100/40 p-2">
            <TrendingUp className="size-5 text-blue-600" strokeWidth={2.5} />
          </div>
          <CardTitle className="text-base font-semibold">Content Velocity Trend</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">Industry Avg.</p>
      </CardHeader>
      <CardContent className="pt-0">
        <VelocityContent status={status} items={items} max={max} />
      </CardContent>
    </Card>
  );
}

function VelocitySkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 5 }, (_, i) => (
        <div key={i} className="space-y-2">
          <div className="flex items-baseline justify-between">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
        </div>
      ))}
    </div>
  );
}
